import {cancelledUpload} from '../utils/completionState';
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, LinearProgress, Stack, Typography } from '@mui/joy';
import { API_URL } from '../api';
import { checkSignal, retryUploadRequest, surveyorFileId, uploadJson, uploadSurveyorFile, waitUpload } from '../utils/surveyorUpload';

const bytes = n => `${(n / 1024 / 1024).toFixed(1)} МБ`;
const readPlan = id => {
  try {
    const plan = JSON.parse(localStorage.getItem(`surveyor-upload-plan:${id}`) || '[]');
    return Array.isArray(plan) ? plan.filter(x => x && typeof x.upload_id === 'string' && typeof x.file_name === 'string') : [];
  } catch { return []; }
};
const savePlan = (id, plan) => { try { localStorage.setItem(`surveyor-upload-plan:${id}`, JSON.stringify(plan)); } catch { /* сервер хранит начатые файлы */ } };

export default function SurveyorUploadProgress({ uploadData, onDone, onBusyChange, onBack, supplement = false, waitForProcessing = true }) {
  const entries = useRef(null);
  if (!entries.current) entries.current = uploadData.files
    ? uploadData.files.map((file, i) => ({ file, type: uploadData.fileTypes?.[i] || 'room_photo' }))
    : [...uploadData.sheetFiles.map(x => ({ file: x.file, type: 'survey_sheet' })),
       ...uploadData.photoFiles.map(x => ({ file: x.file, type: 'room_photo' }))];
  const initial = useRef(uploadData), controller = useRef(null), flight = useRef(false), mounted = useRef(false);
  const scopeValid = useRef(true);
  scopeValid.current = initial.current.token === uploadData.token && String(initial.current.eventId) === String(uploadData.eventId);
  const processId = useRef(uploadData.resumeProcessId || null);
  const processFailed = useRef(false), pendingTransfer = useRef(false);
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [hint, setHint] = useState('Подготавливаем загрузку…');
  const [stage, setStage] = useState('uploading');
  const [retryableProcessing, setRetryableProcessing] = useState(false);
  const [progress, setProgress] = useState({ index: 0, loaded: 0, confirmed: 0 });
  const total = entries.current.reduce((s, x) => s + x.file.size, 0);
  const callbacks = useRef({ onDone, onBusyChange, onBack }); callbacks.current = { onDone, onBusyChange, onBack };
  const update = fn => { if (mounted.current && scopeValid.current) fn(); };

  async function run(retryProcessing = false) {
    if (flight.current || !scopeValid.current) return;
    flight.current = true; controller.current = new AbortController();
    const signal = controller.current.signal, data = initial.current, token = data.token;
    update(() => { setBusy(true); setError(''); }); callbacks.current.onBusyChange?.(true);
    const onRetry = info => update(() => setHint(info?.message || 'Связь прервалась. Повторяем автоматически с сохранённого места…'));
    const json = (path, body) => retryUploadRequest(() => uploadJson(API_URL, path, { token, data: body, signal }), { signal, onRetry });
    try {
      if (!processId.current) {
        validateFiles();
        let start;
        if (supplement && data.additionalCycleId !== undefined) {
          // Допобмер использует действующую проверку цикла, без обхода её API.
          const { APISupplementEventSurveyor } = await import('../api');
          start = await APISupplementEventSurveyor(data.eventId, data.comment || 'Дополнение обмера', token, data.additionalCycleId);
        } else {
          start = await json(`events/${data.eventId}/${supplement ? 'supplement' : 'complete'}/`, { comment: data.comment || 'Дополнение обмера' });
        }
        checkSignal(signal);
        if (['uploading', 'pending', 'processing'].includes(start?.process_status) && start?.process_id) start = { ...start, status: 'ok' };
        if (start?.status !== 'ok' || !start.process_id) throw new Error(start?.message || 'Не удалось создать загрузку. Повторите попытку.');
        processId.current = start.process_id;
      }
      const id = processId.current;
      const state = await json(`events/${data.eventId}/process-status/`);
      const matching = String(state?.process_id) === String(id);
      if (state?.code >= 400 || !matching) throw new Error(state?.message || 'Не удалось проверить текущую загрузку. Повторите попытку.');
      if ((supplement && data.additionalCycleId === undefined && state.process_type && state.process_type !== 'supplement') ||
          (!supplement && state.process_type && state.process_type !== 'completion')) {
        throw new Error('По событию запущен другой вид загрузки. Откройте соответствующее дополнение или завершение обмера.');
      }
      rememberTransfer(state);
      if (cancelledUpload(state)) {
        processFailed.current = true;
        stageRef.current = 'uploading';
        update(() => { setStage('uploading'); setRetryableProcessing(false); });
        throw new Error('Эта загрузка отменена. Вернитесь к выбору файлов, чтобы начать новую попытку.');
      }
      if (state.status === 'failed') {
        if (retryProcessing === true && state.retryable) {
          const result = await json(`${supplement ? 'supplement' : 'completion'}/${id}/retry/`, {});
          if (result?.status !== 'ok') throw new Error(result?.message || 'Не удалось повторить завершение. Проверьте статус ещё раз.');
          processFailed.current = false;
          update(() => setRetryableProcessing(false));
          await afterFinish(id, signal); return;
        }
        processingError(state);
      }
      if (matching && ['pending', 'processing', 'done'].includes(state.status)) {
        await afterFinish(id, signal); return;
      }
      validateFiles();
      stageRef.current = 'uploading';
      update(() => setStage('uploading'));
      const saved = readPlan(id);
      const remote = matching && Array.isArray(state.uploads) ? state.uploads : [];
      const plan = [];
      for (const entry of entries.current) {
        checkSignal(signal);
        const uploadId = await surveyorFileId(id, entry.file, entry.type, signal, percent => update(() => setHint(`Подготовка ${entry.file.name}: ${percent}%`)));
        plan.push({ upload_id: uploadId, file_name: entry.file.name, total_size: entry.file.size, file_type: entry.type, entry });
      }
      const ids = new Set(plan.map(x => x.upload_id));
      // При восстановлении не завершать заказ с забытыми, ещё не выбранными файлами.
      const expected = [...saved, ...remote];
      const missing = expected.filter(x => !ids.has(x.upload_id) && !remote.some(r => r.upload_id === x.upload_id && r.state === 'ready'));
      if (missing.length) {
        const names = [...new Set(missing.map(x => x.file_name))];
        throw new Error(`Для продолжения выберите исходные файлы с прежними именами и содержимым. Не выбраны: ${names.slice(0, 5).join(', ')}${names.length > 5 ? ` и ещё ${names.length - 5}` : ''}. Нажмите «Вернуться к выбору файлов». Подтверждённые файлы повторно отправлять не нужно.`);
      }
      const finalIds = [...new Set([...plan.map(x => x.upload_id), ...remote.filter(x => x.state === 'ready').map(x => x.upload_id)])];
      savePlan(id, [...plan.map(({upload_id, file_name, total_size, file_type}) => ({upload_id, file_name, total_size, file_type})), ...remote.filter(x => x.state === 'ready' && !ids.has(x.upload_id))]);
      // Подготовить все файлы до передачи: сервер знает и о ещё не начатых.
      for (const item of plan) {
        const init = await json('chunked-upload/init/', { upload_id: item.upload_id, process_id: id,
          file_name: item.file_name, file_type: item.file_type, total_size: item.total_size, mime_type: item.entry.file.type || '' });
        if (init?.status !== 'ok') throw new Error(`Подготовка «${item.file_name}»: ${init?.message || 'сервер не подтвердил загрузку'}${init?.code ? ` (HTTP ${init.code})` : ''}`);
        item.prepared = init;
      }
      let confirmed = 0;
      for (let index = 0; index < plan.length; index++) {
        const item = plan[index];
        update(() => { setHint('Загружаем файл небольшими частями…'); setProgress({ index, confirmed, loaded: 0 }); });
        const result = await uploadSurveyorFile(API_URL, { processId: id, file: item.entry.file, fileType: item.file_type,
          uploadId: item.upload_id, prepared: item.prepared, token, signal, onRetry,
          onProgress: ({ loaded, transport }) => update(() => { setHint(transport === 'main-fallback' ? 'Сохраняем на основном сервере. Перенос в домашнее хранилище выполнится автоматически.' : 'Загружаем файл небольшими частями…'); setProgress({ index, confirmed, loaded }); }) });
        if (result?.status !== 'ok') throw new Error(`Файл «${item.file_name}»: ${result?.message || 'не удалось передать данные'}${result?.code ? ` (HTTP ${result.code})` : ''}`);
        if (result.transfer_state === 'pending' || result.transfer_pending) pendingTransfer.current = true;
        confirmed += item.total_size;
      }
      update(() => { setProgress({ index: plan.length - 1, confirmed, loaded: 0 }); setHint('Все файлы на сервере. Завершаем загрузку…'); });
      const finished = await json(`${supplement ? 'supplement' : 'completion'}/${id}/finish-upload/`, { upload_ids: finalIds });
      if (finished?.status !== 'ok') throw new Error(finished?.message || 'Файлы сохранены, но запуск обработки не подтверждён. Нажмите «Продолжить».');
      if (finished.transfer_state === 'pending' || finished.transfer_pending) pendingTransfer.current = true;
      await afterFinish(id, signal);
    } catch (e) {
      update(() => { setError(e.message || 'Ошибка связи. Можно продолжить загрузку.'); setHint(stageRef.current === 'processing' ? 'Переданные файлы сохранены. Повторная загрузка не требуется.' : 'Полученные сервером части сохранены.'); });
    } finally {
      flight.current = false;
      update(() => setBusy(false)); if (scopeValid.current) callbacks.current.onBusyChange?.(false);
    }
  }

  const stageRef = useRef('uploading');
  function validateFiles() {
    if (!entries.current.length || entries.current.length > 500 || entries.current.some(x => !x.file.size || x.file.size > 2 * 1024 ** 3)) {
      throw new Error('Выберите от 1 до 500 файлов. Каждый файл должен быть непустым и не больше 2 ГБ.');
    }
  }
  function rememberTransfer(state) {
    if (Object.prototype.hasOwnProperty.call(state || {}, 'transfer_pending')) pendingTransfer.current = Boolean(state.transfer_pending);
    else if (state?.transfer_state) pendingTransfer.current = state.transfer_state === 'pending';
  }
  function processingError(state) {
    processFailed.current = true;
    if (cancelledUpload(state)) {
      stageRef.current = 'uploading';
      update(() => { setStage('uploading'); setRetryableProcessing(false); });
      throw new Error('Эта загрузка отменена. Вернитесь к выбору файлов, чтобы начать новую попытку.');
    }
    stageRef.current = 'processing';
    update(() => { setStage('processing'); setRetryableProcessing(Boolean(state.retryable)); });
    throw new Error(state.retry_reason || state.error_message || 'Не удалось завершить обработку. Файлы сохранены; сообщите менеджеру номер загрузки.');
  }
  async function afterFinish(id, signal) {
    const data = initial.current;
    stageRef.current = 'processing';
    update(() => { setStage('processing'); setHint('Файлы сохранены. Подтверждаем завершение обмера…'); });
    if (waitForProcessing) {
      // Завершение подтверждает сервер, а не один лишь приём файлов.
      // После паузы продолжаем проверять тот же процесс без повторной передачи.
      for (let n = 0; n < 120; n++) {
        checkSignal(signal);
        const state = await retryUploadRequest(() => uploadJson(API_URL,
          `${supplement ? 'supplement' : 'completion'}/${id}/status/`, { token: data.token, signal }), { signal });
        checkSignal(signal);
        rememberTransfer(state);
        if (state?.code >= 400) throw new Error(state.message || 'Не удалось проверить завершение. Нажмите «Проверить завершение».');
        if (state.status === 'done') break;
        if (state.status === 'failed') processingError(state);
        if (state?.code >= 400 || !['pending', 'processing'].includes(state.status)) {
          throw new Error(state.message || 'Не удалось проверить завершение. Нажмите «Проверить завершение» — файлы уже сохранены.');
        }
        if (n === 119) throw new Error('Завершение ещё обрабатывается. Нажмите «Проверить завершение». Повторно загружать файлы не нужно.');
        await waitUpload(3000, signal);
      }
      if (supplement) window.dispatchEvent(new Event('surveyor-additional-changed'));
    }
    try { localStorage.removeItem(`surveyor-upload-plan:${id}`); } catch {}
    if (mounted.current && scopeValid.current) callbacks.current.onDone?.({ success: true, process_id: id,
      process_status: waitForProcessing ? 'done' : 'pending', transfer_pending: pendingTransfer.current });
  }

  useEffect(() => {
    mounted.current = true;
    const timer = setTimeout(() => run(initial.current.retryProcessing === true), 0);
    return () => { mounted.current = false; clearTimeout(timer); controller.current?.abort(); };
  }, []);
  useEffect(() => {
    if (!scopeValid.current) { controller.current?.abort(); callbacks.current.onBusyChange?.(false); }
  }, [uploadData.token, uploadData.eventId]);
  useEffect(() => {
    if (!busy || !scopeValid.current) return;
    const prevent = e => { e.preventDefault(); e.returnValue = ''; };
    let lock, stopped = false;
    const acquire = async () => {
      if (document.visibilityState !== 'visible' || stopped || lock) return;
      try { const result = await navigator.wakeLock?.request('screen'); if (stopped) result?.release(); else { lock = result; lock?.addEventListener('release', () => { lock = null; }); } } catch {}
    };
    acquire(); document.addEventListener('visibilitychange', acquire); window.addEventListener('beforeunload', prevent);
    return () => { stopped = true; lock?.release(); document.removeEventListener('visibilitychange', acquire); window.removeEventListener('beforeunload', prevent); };
  }, [busy, uploadData.token, uploadData.eventId]);
  const loaded = Math.min(total, progress.confirmed + progress.loaded), percent = total ? Math.min(busy ? 99 : 100, Math.floor(loaded * 100 / total)) : 0;
  if (!scopeValid.current) return <Alert color="warning">Сессия или событие изменились. Откройте страницу загрузки заново, чтобы продолжить.</Alert>;
  return <Stack spacing={1.8} sx={{ py: 2 }} aria-label="Загрузка материалов обмера">
    <Typography level="title-md">{stage === 'processing' ? (busy ? 'Завершаем обмер' : 'Завершение не подтверждено') : (busy ? 'Загрузка материалов' : 'Загрузка приостановлена')}</Typography>
    {stage === 'uploading' && <>
      <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>{entries.current[progress.index]?.file.name}</Typography>
      <LinearProgress determinate value={percent} aria-label="Прогресс загрузки" />
      <Typography level="body-xs">Файл {Math.min(progress.index + 1, entries.current.length)} из {entries.current.length} · {bytes(loaded)} / {bytes(total)}</Typography>
    </>}
    <Typography level="body-sm" role="status">{hint}</Typography>
    {processId.current && <Typography level="body-xs">Загрузка №{processId.current}</Typography>}
    {error && <Alert color="warning" sx={{ overflowWrap: 'anywhere' }}>{error}</Alert>}
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      {busy ? <Button variant="outlined" color="neutral" onClick={() => controller.current?.abort()}>{stage === 'processing' ? 'Остановить ожидание' : 'Приостановить'}</Button>
        : <Button onClick={() => run(retryableProcessing)}>{stage === 'processing' ? (retryableProcessing ? 'Повторить завершение' : 'Проверить завершение') : 'Продолжить загрузку'}</Button>}
      {!busy && stage !== 'processing' && (!supplement || onBack) && <Button variant="plain" color="neutral" onClick={() => {
        const result = {success:false, process_id:processFailed.current ? null : processId.current};
        if (callbacks.current.onBack) callbacks.current.onBack(result); else callbacks.current.onDone?.(result);
      }}>Вернуться к выбору файлов</Button>}
    </Box>
    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>{stage === 'processing' ? 'Файлы приняты. Ожидаем подтверждения завершения события; перенос в домашнее хранилище при необходимости продолжится в фоне.' : 'Оставьте страницу открытой до передачи файлов. При обрыве повторится только неполученная часть. После закрытия страницы нужно заново выбрать те же файлы.'}</Typography>
  </Stack>;
}
